import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Plus, Search } from "lucide-react";
import { PageHeader } from "@/components/layout/PageHeader";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card } from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { DataTable, type Column } from "@/components/data/DataTable";
import { UserAvatar } from "@/components/user/UserAvatar";
import { IntegrationIcons, RoleBadge, StatusPill } from "@/components/user/UserBadges";
import { UserActions } from "@/components/user/UserActions";
import { LocationBadge, locationFromUser } from "@/components/location/LocationDisplay";
import { RelTime } from "@/components/data/RelTime";
import { fmtCurrency, fmtNumber } from "@/lib/format";
import { useUsers } from "@/lib/queries";
import { authClient } from "@/lib/auth";
import { toast } from "sonner";
import { useQueryClient } from "@tanstack/react-query";
import type { AdminUser } from "@/types";
import { cn } from "@/lib/utils";

const FILTERS = [
  { id: "all", label: "All" },
  { id: "admins", label: "Admins" },
  { id: "banned", label: "Banned" },
  { id: "active7d", label: "Active 7d" },
  { id: "located", label: "Has location" },
  { id: "ios", label: "Has iOS" },
  { id: "telegram", label: "Has Telegram" },
  { id: "whatsapp", label: "Has WhatsApp" },
] as const;

// Includes the ascending variants a second header click produces, so the
// Select always has a matching label.
const SORT_OPTIONS = [
  { value: "createdAt:desc", label: "Recent signup" },
  { value: "createdAt:asc", label: "Oldest signup" },
  { value: "last_active_at:desc", label: "Last active" },
  { value: "last_active_at:asc", label: "Least recently active" },
  { value: "last_location_at:desc", label: "Last location" },
  { value: "last_location_at:asc", label: "Oldest location" },
  { value: "expense_count:desc", label: "Most entries" },
  { value: "expense_count:asc", label: "Fewest entries" },
  { value: "email:asc", label: "Email A→Z" },
  { value: "email:desc", label: "Email Z→A" },
] as const;

const PAGE_SIZE = 50;

export function UsersPage() {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [params, setParams] = useSearchParams();
  const urlSearch = params.get("search") ?? "";
  const initialSearch = urlSearch;
  const [searchInput, setSearchInput] = useState(initialSearch);
  const [debouncedSearch, setDebouncedSearch] = useState(initialSearch);
  const [filter, setFilter] = useState<(typeof FILTERS)[number]["id"]>("all");
  const [sortVal, setSortVal] = useState<string>(SORT_OPTIONS[0].value);
  const [page, setPage] = useState(0);
  const [createOpen, setCreateOpen] = useState(false);

  // Debounce search
  useEffect(() => {
    const t = setTimeout(() => {
      setDebouncedSearch(searchInput.trim());
      setPage(0);
    }, 300);
    return () => clearTimeout(t);
  }, [searchInput]);

  // Follow the URL when something else changes it (e.g. the top-bar search
  // while this page is already open).
  useEffect(() => {
    if (urlSearch === debouncedSearch) return;
    setSearchInput(urlSearch);
    setDebouncedSearch(urlSearch);
    setPage(0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [urlSearch]);

  // Persist search in URL for share-ability
  useEffect(() => {
    const next = new URLSearchParams(params);
    if (debouncedSearch) next.set("search", debouncedSearch);
    else next.delete("search");
    if ((params.get("search") ?? "") !== debouncedSearch) setParams(next, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedSearch]);

  const [sortKey, sortDir] = useMemo(() => sortVal.split(":") as [string, "asc" | "desc"], [sortVal]);

  const query = useUsers({
    limit: PAGE_SIZE,
    offset: page * PAGE_SIZE,
    search: debouncedSearch || undefined,
    sort: sortKey,
    dir: sortDir,
  });

  // Apply client-side filter chips on top of the API result
  const filteredUsers = useMemo(() => {
    const all = query.data?.users ?? [];
    if (filter === "all") return all;
    const sevenDays = Date.now() - 7 * 24 * 60 * 60 * 1000;
    return all.filter((u) => {
      if (filter === "admins") return u.role === "admin";
      if (filter === "banned") return !!u.banned;
      if (filter === "ios") return !!u.has_ios_device;
      if (filter === "telegram") return !!u.has_telegram;
      if (filter === "whatsapp") return !!u.has_whatsapp;
      if (filter === "located") return !!locationFromUser(u);
      if (filter === "active7d") {
        const v = u.last_active_at;
        if (v == null) return false;
        const ms = typeof v === "number" ? (v < 1e12 ? v * 1000 : v) : Date.parse(String(v));
        return Number.isFinite(ms) && ms >= sevenDays;
      }
      return true;
    });
  }, [query.data, filter]);

  const total = query.data?.total ?? 0;

  const columns: Column<AdminUser>[] = [
    {
      key: "user",
      header: "User",
      cell: (u) => (
        <div className="flex items-center gap-2.5">
          <UserAvatar size="sm" name={u.name} email={u.email} image={u.image} />
          <Link
            to={`/users/${u.id}`}
            className="min-w-0 rounded hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          >
            <div className="truncate text-sm font-medium">{u.name ?? u.email}</div>
            <div className="truncate font-mono text-[11px] text-muted-foreground">{u.email}</div>
          </Link>
        </div>
      ),
    },
    {
      key: "role",
      header: "Role",
      cell: (u) => <RoleBadge role={u.role} />,
      width: 90,
    },
    {
      key: "status",
      header: "Status",
      cell: (u) => <StatusPill user={u} />,
      width: 90,
    },
    {
      key: "integrations",
      header: "Integrations",
      cell: (u) => <IntegrationIcons user={u} />,
      width: 110,
    },
    {
      key: "location",
      header: "Location",
      cell: (u) => <LocationBadge location={locationFromUser(u)} showTime />,
      sortable: true,
      sortKey: "last_location_at",
      width: 190,
    },
    {
      key: "expense_count",
      header: "Entries",
      cell: (u) => <span className="tabular-nums text-foreground/80">{fmtNumber(u.expense_count)}</span>,
      sortable: true,
      sortKey: "expense_count",
      width: 100,
    },
    {
      key: "expense_sum",
      header: "Spent",
      // Spending in the user's home currency only; other-currency rows aren't added in.
      cell: (u) =>
        u.home_currency ? (
          <span
            className="tabular-nums text-foreground/80"
            title={u.has_other_currencies ? `Only ${u.home_currency} entries; some entries are in other currencies` : undefined}
          >
            {fmtCurrency(u.expense_sum, u.home_currency, { compact: true })}
            {u.has_other_currencies ? "*" : ""}
          </span>
        ) : (
          <span className="text-muted-foreground">—</span>
        ),
      width: 110,
    },
    {
      key: "last_active",
      header: "Last active",
      cell: (u) => (
        <span className="text-xs text-muted-foreground">
          {u.last_active_at ? <RelTime value={u.last_active_at} /> : "—"}
        </span>
      ),
      sortable: true,
      sortKey: "last_active_at",
      width: 130,
    },
    {
      key: "createdAt",
      header: "Signed up",
      cell: (u) => (
        <span className="text-xs text-muted-foreground">
          <RelTime value={u.createdAt} />
        </span>
      ),
      sortable: true,
      sortKey: "createdAt",
      width: 130,
    },
    {
      key: "actions",
      header: "",
      cell: (u) => (
        <div className="flex justify-end" onClick={(e) => e.stopPropagation()}>
          <UserActions user={u} />
        </div>
      ),
      width: 50,
      className: "text-right",
    },
  ];

  return (
    <>
      <PageHeader
        title="Users"
        description={
          query.isError && !query.data
            ? "Couldn't load users"
            : query.data
              ? `${fmtNumber(total)} total · showing ${filteredUsers.length}`
              : "Loading…"
        }
        actions={
          <Button size="sm" onClick={() => setCreateOpen(true)}>
            <Plus className="h-4 w-4" /> Create user
          </Button>
        }
      />

      <Card className="mb-4 p-3">
        <div className="flex flex-wrap items-center gap-3">
          <div className="relative min-w-[220px] flex-1">
            <Search className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
            <Input
              placeholder="Search email, name, or id…"
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              className="h-9 pl-8"
            />
          </div>
          <div className="flex flex-wrap gap-1.5" role="group" aria-label="Filter users">
            {FILTERS.map((f) => (
              <button
                key={f.id}
                type="button"
                aria-pressed={filter === f.id}
                onClick={() => setFilter(f.id)}
                className={cn(
                  "rounded-full px-3 py-1 text-xs transition-colors",
                  filter === f.id
                    ? "bg-foreground text-background"
                    : "bg-secondary/60 text-muted-foreground hover:text-foreground"
                )}
              >
                {f.label}
              </button>
            ))}
          </div>
          <div className="ml-auto min-w-[180px]">
            <Select
              value={sortVal}
              onValueChange={(v) => {
                setSortVal(v);
                setPage(0);
              }}
            >
              <SelectTrigger className="h-9" aria-label="Sort users">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {SORT_OPTIONS.map((o) => (
                  <SelectItem key={o.value} value={o.value}>
                    {o.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
        {filter !== "all" && (
          <div className="mt-2 text-[11px] text-muted-foreground">
            <Badge variant="outline" className="mr-1.5 font-mono">
              filter: {filter}
            </Badge>
            client-side filter applied to current page
          </div>
        )}
      </Card>

      <DataTable
        columns={columns}
        rows={filteredUsers}
        loading={query.isLoading}
        error={query.isError ? query.error : undefined}
        onRetry={() => query.refetch()}
        rowKey={(u) => u.id}
        onRowClick={(u) => navigate(`/users/${u.id}`)}
        pageSize={PAGE_SIZE}
        totalRows={total}
        page={page}
        onPageChange={setPage}
        sortKey={sortKey}
        sortDir={sortDir}
        onSortChange={(k, d) => {
          setSortVal(`${k}:${d}`);
          setPage(0);
        }}
        emptyTitle="No users match"
        emptyDescription={debouncedSearch ? `No results for "${debouncedSearch}".` : "Create the first one."}
      />

      <CreateUserDialog
        open={createOpen}
        onOpenChange={setCreateOpen}
        onCreated={() => {
          qc.invalidateQueries({ queryKey: ["users"] });
          qc.invalidateQueries({ queryKey: ["overview"] });
        }}
      />
    </>
  );
}

function CreateUserDialog({
  open,
  onOpenChange,
  onCreated,
}: {
  open: boolean;
  onOpenChange: (o: boolean) => void;
  onCreated: () => void;
}) {
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);

  const reset = () => {
    setEmail("");
    setName("");
    setPassword("");
  };

  const submit = async () => {
    setBusy(true);
    try {
      const res = await authClient.admin.createUser({
        email: email.trim(),
        name: name.trim() || email.trim(),
        password,
        role: "user",
      });
      if ("error" in res && res.error) throw new Error(res.error.message ?? "Failed");
      toast.success("User created");
      onCreated();
      reset();
      onOpenChange(false);
    } catch (err) {
      toast.error("Create failed", { description: err instanceof Error ? err.message : "Unknown error" });
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Create user</DialogTitle>
          <DialogDescription>Creates an account directly. The new user is sent the usual welcome email.</DialogDescription>
        </DialogHeader>
        <p className="text-xs text-muted-foreground">
          Admin privileges can only be granted in SQL: <code className="font-mono">UPDATE "user" SET role = 'admin' WHERE email = …</code>
        </p>
        <div className="space-y-3">
          <div className="space-y-1.5">
            <Label htmlFor="cu-email">Email</Label>
            <Input id="cu-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="cu-name">Name</Label>
            <Input id="cu-name" value={name} onChange={(e) => setName(e.target.value)} />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="cu-pw">Password</Label>
            <Input id="cu-pw" type="text" value={password} onChange={(e) => setPassword(e.target.value)} className="font-mono" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={busy}>
            Cancel
          </Button>
          <Button disabled={!email || password.length < 8 || busy} onClick={submit}>
            {busy ? "Creating…" : "Create user"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
